/**
 * Debounce and throttle helpers for high-frequency DOM events.
 */

/**
 * Returns a function that delays invoking `fn` until after `waitMs` of inactivity.
 * @template {(...args: any[]) => any} T
 * @param {T} fn
 * @param {number} waitMs
 * @returns {((...args: Parameters<T>) => void) & { cancel: () => void }}
 */
export function debounce(fn, waitMs) {
  let timerId = null;
  const debounced = (...args) => {
    if (timerId != null) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => {
      timerId = null;
      fn(...args);
    }, waitMs);
  };
  debounced.cancel = () => {
    if (timerId != null) {
      clearTimeout(timerId);
      timerId = null;
    }
  };
  return debounced;
}

/**
 * Returns a function that invokes `fn` at most once every `waitMs`.
 * Leading-edge: the first call in a window runs immediately.
 * @template {(...args: any[]) => any} T
 * @param {T} fn
 * @param {number} waitMs
 * @returns {((...args: Parameters<T>) => void) & { cancel: () => void }}
 */
export function throttle(fn, waitMs) {
  let lastCall = 0;
  let timerId = null;
  let pendingArgs = null;

  const throttled = (...args) => {
    const now = Date.now();
    const remaining = waitMs - (now - lastCall);
    if (remaining <= 0) {
      if (timerId != null) {
        clearTimeout(timerId);
        timerId = null;
      }
      lastCall = now;
      pendingArgs = null;
      fn(...args);
      return;
    }
    pendingArgs = args;
    if (timerId == null) {
      timerId = setTimeout(() => {
        lastCall = Date.now();
        timerId = null;
        const queued = pendingArgs;
        pendingArgs = null;
        if (queued) fn(...queued);
      }, remaining);
    }
  };

  throttled.cancel = () => {
    if (timerId != null) {
      clearTimeout(timerId);
      timerId = null;
    }
    pendingArgs = null;
  };
  return throttled;
}
